import React, { FC, useRef, useEffect } from 'react'
import lazyWithPreload from 'utils/lazyWithPreload'
import styled, { css } from 'styled-components'

import { useHistory } from 'react-router-dom'
import { useDelayedMarkCommentsOrRepliesAsRead } from '../../operations'
import { useThreadRouteParams } from 'utils/hooks/useThreadRouteParams'
import ActivityComment from '../ActivityComment'
import { CommentInfoFragment } from 'gqlTypes'
import { FlatButton } from 'components/Button/FlatButton'
import { ReactComponent as ArrowLeftIcon } from 'icons/arrow-left-12.svg'
import { ThreadSubscriptionToggle } from 'modules/shares/components/ShareMetaSidebar/SubscriptionToggle'
import { Flex } from 'components/Box'
import { ArtboardInSidebar } from 'modules/shares/types'
import { routes } from 'constants/routes'

const Comment = lazyWithPreload(
  () => import('components/Comment') as any
) as FC<unknown>

// Types
interface ThreadProps {
  artboard: ArtboardInSidebar
  comment: CommentInfoFragment
  shareIdentifier: string
}

const LinkButton = styled(FlatButton)`
  font-size: 0.6875rem;
  text-transform: uppercase;
  text-align: left;
  margin: 8px 0 4px;
  padding: 6px 16px 7px; /* stylelint-disable-line scales/space */
`

const ArrowLeft = styled(ArrowLeftIcon)`
  width: 12px;
  height: 10px;
  margin-right: 5px; /* stylelint-disable-line scales/space */
  margin-bottom: -1px; /* stylelint-disable-line scales/space */
`

const ToggleContainer = styled.div`
  padding: ${({ theme }) => css`
    0 ${theme.space[4]}px ${theme.space[4]}px
  `};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.dark.base};
`

const ScrollContainer = styled.div`
  height: 100%;
  width: 100%;
  overflow: auto;
`

export const ThreadPanel: FC<ThreadProps> = ({
  comment,
  artboard,
  shareIdentifier,
}) => {
  const { push } = useHistory()
  const { replyIdentifier } = useThreadRouteParams()

  const repliesEndRef = useRef<HTMLDivElement>(null)
  const firstLoad = useRef<boolean>(true)

  const { permanentArtboardShortId, documentVersionShortId } = artboard
  const reversedReplies = [...comment.replies].reverse()
  const firstUnreadReply = reversedReplies.find(({ isRead }) => !isRead)

  // This is necessary for when there are unread replies and after X seconds,
  // they are marked as read. In this case we want to stick with the scroll
  // positioned on the first unread reply.
  const initialNumberOfReplies = useRef<number>(reversedReplies.length)

  // Marks replies as read after 2 seconds
  useDelayedMarkCommentsOrRepliesAsRead({
    parentComment: comment,
    activities: comment.replies as CommentInfoFragment[],
    permanentArtboardShortId: artboard.permanentArtboardShortId,
    shareIdentifier,
  })

  const scrollToBottom = () => {
    window.requestAnimationFrame(() => {
      if (!repliesEndRef.current?.scrollIntoView) return

      repliesEndRef.current.scrollIntoView()
    })
  }

  /**
   * Scroll behavior
   * - On load, if reply ID is on the URL, scrolls to this reply
   * - On load, in case of unread replies, scrolls to the first unread reply
   * - On load, if no unread replies, scrolls to the last reply
   * - Sending new reply scrolls to the last reply
   */
  useEffect(() => {
    if (firstLoad.current) {
      firstLoad.current = false

      // Scroll to the first unread comment
      if (firstUnreadReply) {
        window.requestAnimationFrame(() => {
          document.getElementById(firstUnreadReply.identifier)?.scrollIntoView()
        })

        return
      }

      // Reply ID on the URL
      if (replyIdentifier) {
        setTimeout(
          () => document.getElementById(replyIdentifier)?.scrollIntoView(),
          0
        )
        return
      }

      // Any other case, scroll to the bottom
      setTimeout(() => scrollToBottom(), 0)
    } else {
      // When new replies arrive via subscriptions
      if (reversedReplies.length !== initialNumberOfReplies.current) {
        setTimeout(() => scrollToBottom(), 0)
      }
    }
  }, [reversedReplies, replyIdentifier, firstUnreadReply])

  return (
    <>
      <div>
        <LinkButton
          onClick={() =>
            push(
              routes.ARTBOARD_DETAIL.create({
                shareID: shareIdentifier,
                permanentArtboardShortId,
              })
            )
          }>
          <ArrowLeft />
          All Comments
        </LinkButton>
      </div>

      <ToggleContainer>
        <ThreadSubscriptionToggle
          id={comment.identifier}
          shareIdentifier={shareIdentifier}
          status={comment.subscriptionStatus}
          documentVersionShortId={documentVersionShortId}
          permanentArtboardShortId={permanentArtboardShortId}
        />
      </ToggleContainer>

      <ScrollContainer>
        <ActivityComment
          identifier={comment.identifier}
          isEdited={!!comment.isEdited}
          isRead={!!comment.isRead}
          body={comment.body!}
          user={comment.user}
          createdAt={comment.createdAt!}
          updatedAt={comment.updatedAt!}
          userIsCreator={comment.userIsCreator}
          userCanDelete={comment.userCanDelete}
          Comment={Comment}
          permanentArtboardShortId={permanentArtboardShortId}
          documentVersionShortId={documentVersionShortId}
          shareIdentifier={shareIdentifier}
        />
        <Flex flexDirection="column" paddingLeft={5}>
          {reversedReplies.map(reply => (
            <ActivityComment
              key={reply.identifier}
              identifier={reply.identifier}
              isEdited={!!reply.isEdited}
              isRead={!!reply.isRead}
              body={reply.body!}
              user={reply.user}
              createdAt={reply.createdAt!}
              updatedAt={reply.updatedAt!}
              userIsCreator={reply.userIsCreator}
              userCanDelete={reply.userCanDelete}
              Comment={Comment}
              permanentArtboardShortId={permanentArtboardShortId}
              documentVersionShortId={documentVersionShortId}
              parentCommentIdentifier={comment.identifier}
              shareIdentifier={shareIdentifier}
            />
          ))}
          {/* And empty div to scroll down to the end of the list */}
          <div ref={repliesEndRef} />
        </Flex>
      </ScrollContainer>
    </>
  )
}
